
import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import PageFooter from '../../components/feature/PageFooter';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

interface SkillBarProps {
  name: string;
  percent: number;
  color: string;
  visible: boolean;
  delay: number;
}

function SkillBar({ name, percent, color, visible, delay }: SkillBarProps) {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (visible) {
      const timer = setTimeout(() => setWidth(percent), delay);
      return () => clearTimeout(timer);
    } else {
      setWidth(0);
    }
  }, [visible, percent, delay]);

  return (
    <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: `${delay}ms` }}>
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-semibold text-gray-800">{name}</span>
        <span className="text-sm font-bold text-orange-600">{percent}%</span>
      </div>
      <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-1000 ease-out ${color}`}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}

export default function SkillsPage() {
  const [loaded, setLoaded] = useState(false);
  const { ref: dataRef, isVisible: dataVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: langRef, isVisible: langVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: certRef, isVisible: certVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: legalRef, isVisible: legalVisible } = useScrollAnimation({ threshold: 0.1 });

  useEffect(() => {
    setLoaded(true);
  }, []);

  const dataSkills = [
    { name: 'Python', percent: 65, color: 'bg-gradient-to-r from-orange-400 to-orange-500' },
    { name: 'SQL', percent: 70, color: 'bg-gradient-to-r from-orange-400 to-rose-400' },
    { name: 'Excel / Google Sheets', percent: 50, color: 'bg-gradient-to-r from-rose-400 to-rose-500' },
    { name: 'Tableau / Power BI', percent: 50, color: 'bg-gradient-to-r from-amber-400 to-orange-400' },
    { name: 'Statistics & Data Analysis', percent: 60, color: 'bg-gradient-to-r from-orange-500 to-rose-500' },
    { name: 'Project Management', percent: 60, color: 'bg-gradient-to-r from-orange-500 to-rose-500' },
    { name: 'Git', percent: 60, color: 'bg-gradient-to-r from-orange-500 to-rose-500' },
    { name: 'ELK/Splunk', percent: 40, color: 'bg-gradient-to-r from-orange-500 to-rose-500' },
    { name: 'AWS/GCP', percent: 50, color: 'bg-gradient-to-r from-orange-500 to-rose-500' },
    { name: 'Docker/Kubernetes', percent: 40, color: 'bg-gradient-to-r from-orange-500 to-rose-500' },
  ];

  const langSkills = [
    { name: '中文（母語）', percent: 100, color: 'bg-gradient-to-r from-orange-400 to-rose-500' },
    { name: '英文（TOEIC 795）', percent: 75, color: 'bg-gradient-to-r from-amber-400 to-orange-500' },
  ];

  const legalSkills = [
    { name: '法律文件撰寫與審閱', percent: 90, color: 'bg-gradient-to-r from-orange-400 to-rose-400' },
    { name: '法律研究與資料蒐集', percent: 88, color: 'bg-gradient-to-r from-rose-400 to-rose-500' },
    { name: '案件流程管理與跨機關協調', percent: 85, color: 'bg-gradient-to-r from-orange-500 to-rose-500' },
    { name: '溝通與談判', percent: 60, color: 'bg-gradient-to-r from-amber-400 to-orange-400' },
  ];

  const certs = [
    { icon: 'ri-translate-2', title: '多益 TOEIC', score: '795 分', date: '2025.12', color: 'from-orange-400 to-rose-400' },
    { icon: 'ri-bank-line', title: '金融市場常識與職業道德', score: '合格', date: '—', color: 'from-amber-400 to-orange-500' },
    { icon: 'ri-shield-check-line', title: '防制洗錢及打擊資恐職前研習班', score: '合格', date: '—', color: 'from-rose-400 to-rose-600' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-20 px-4 md:px-8 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className={`inline-block px-4 py-2 bg-orange-100 rounded-full mb-6 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="text-sm font-semibold text-orange-700">我的技能</span>
          </div>
          <h1
            className={`text-4xl md:text-6xl font-bold text-gray-900 mb-6 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ fontFamily: 'Playfair Display, serif', transitionDelay: '100ms' }}
          >
            技能與<br />專業能力
          </h1>
          <p
            className={`text-lg text-gray-600 max-w-2xl mx-auto transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '200ms' }}
          >
            結合法律背景與數據分析能力，正積極轉型為數據分析師 / 數據工程師
          </p>
        </div>
      </section>

      {/* Data Skills */}
      <section className="py-20 px-4 md:px-8 bg-white" ref={dataRef}>
        <div className="max-w-5xl mx-auto">
          <div className={`flex items-center gap-4 mb-10 transition-all duration-700 ${dataVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-rose-500 rounded-xl flex items-center justify-center">
              <i className="ri-bar-chart-2-line text-white text-2xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>資料分析 / 工程技能</h2>
              <p className="text-sm text-gray-500">Data Analysis & Engineering</p>
            </div>
          </div>
          <div className="space-y-6">
            {dataSkills.map((skill, i) => (
              <SkillBar key={i} {...skill} visible={dataVisible} delay={i * 100} />
            ))}
          </div>
        </div>
      </section>

      {/* Language Skills */}
      <section className="py-20 px-4 md:px-8 bg-gradient-to-br from-gray-50 to-orange-50" ref={langRef}>
        <div className="max-w-5xl mx-auto">
          <div className={`flex items-center gap-4 mb-10 transition-all duration-700 ${langVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-rose-500 rounded-xl flex items-center justify-center">
              <i className="ri-translate-2 text-white text-2xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>語言能力</h2>
              <p className="text-sm text-gray-500">Language Proficiency</p>
            </div>
          </div>
          <div className="space-y-6">
            {langSkills.map((skill, i) => (
              <SkillBar key={i} {...skill} visible={langVisible} delay={i * 150} />
            ))}
          </div>
        </div>
      </section>

      {/* Legal / Professional Skills */}
      <section className="py-20 px-4 md:px-8 bg-white" ref={legalRef}>
        <div className="max-w-5xl mx-auto">
          <div className={`flex items-center gap-4 mb-10 transition-all duration-700 ${legalVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-rose-500 rounded-xl flex items-center justify-center">
              <i className="ri-scales-3-line text-white text-2xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>法律 / 專業技能</h2>
              <p className="text-sm text-gray-500">Legal & Professional Skills</p>
            </div>
          </div>
          <div className="space-y-6">
            {legalSkills.map((skill, i) => (
              <SkillBar key={i} {...skill} visible={legalVisible} delay={i * 100} />
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 px-4 md:px-8 bg-gradient-to-br from-gray-50 to-orange-50" ref={certRef}>
        <div className="max-w-5xl mx-auto">
          <div className={`flex items-center gap-4 mb-10 transition-all duration-700 ${certVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-rose-500 rounded-xl flex items-center justify-center">
              <i className="ri-medal-line text-white text-2xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>證照與資格</h2>
              <p className="text-sm text-gray-500">Certifications & Qualifications</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {certs.map((cert, i) => (
              <div
                key={i}
                className={`bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl hover:-translate-y-2 transition-all duration-500 ${certVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                <div className={`w-14 h-14 bg-gradient-to-br ${cert.color} rounded-xl flex items-center justify-center mb-4`}>
                  <i className={`${cert.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-2 leading-snug">{cert.title}</h3>
                <div className="flex items-center justify-between mt-3">
                  <span className="inline-block px-3 py-1 bg-orange-100 text-orange-700 text-xs font-bold rounded-full">{cert.score}</span>
                  {cert.date !== '—' && <span className="text-xs text-gray-400">{cert.date}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFooter />
    </div>
  );
}
